import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMore from "../buttons/ExpandMore";
import { AddCircle, ExpandCircleDown, Search } from "@mui/icons-material";
import {
  capitalize,
  cardHeaderSx,
  cardSx,
  mobileCardSx,
  toolbarSearchSx,
} from "utils";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useSearchParams } from "react-router";
export interface CollapsableBoxProps {
  title: string;
  children?: React.ReactNode;
  startExpanded?: boolean;
  expandOnLoad?: boolean;
  padding?: number;
  loading?: boolean;
  searchQuery?: string;
  setSearchQuery?: (value: string) => void;
  createLink?: string;
  onCreateClick?: () => void;
  expandedTitle?: string;
  enableDateRange?: boolean;
  filters?: string[];
  activeFilters?: string[];
  setActiveFilters?: (value: string[]) => void;
}

const CollapsableBox: React.FC<CollapsableBoxProps> = ({
  title,
  children,
  startExpanded = true,
  expandOnLoad = false,
  createLink,
  onCreateClick,
  padding,
  loading = false,
  searchQuery = "",
  setSearchQuery,
  enableDateRange = false,
  expandedTitle,
}) => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down("lg"));
  const [expanded, setExpanded] = useState<boolean>(startExpanded);
  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(7, "day"),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (expandOnLoad && !expanded && !startExpanded && !loading) {
      setExpanded(true);
    }
  }, [expandOnLoad, startExpanded, loading]);

  useEffect(() => {
    if (expandedTitle) {
      if (title === expandedTitle) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }
  }, [expandedTitle, title]);

  useEffect(() => {
    if (enableDateRange) {
      setSearchParams((prev: URLSearchParams) => {
        prev.set("start", startDate?.toISOString() || "");
        prev.set("end", endDate?.toISOString() || "");
        return prev;
      });
    }
  }, [startDate, endDate]);

  return (
    <Card raised={!mobile} sx={mobile ? mobileCardSx : cardSx}>
      <Box
        sx={{
          ...cardHeaderSx,
          px: { xs: 2, md: 1 },
          py: 0,
          height: 60,
          borderBottom: 2,
          borderColor: palette.primary.main,
          alignItems: "center",
        }}
      >
        <Box sx={{ flexGrow: 1, my: "auto" }}>
          <Typography
            variant={mobile ? "h4" : "h3"}
            noWrap
            align="left"
            sx={{ color: palette.secondary.contrastText, pl: 1 }}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", py: 1 }}>
          {enableDateRange && !mobile && (
            <Stack spacing={1} direction={"row"} p={0} mx={2}>
              <DatePicker
                disableFuture
                value={startDate}
                onChange={setStartDate}
                views={["year", "month", "day"]}
                slotProps={{
                  textField: {
                    color: "primary",
                    placeholder: t("select", { name: t("date") }),
                    label: "",
                    margin: "none",
                    sx: toolbarSearchSx,
                    inputProps: {
                      style: { padding: 8.5 },
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                color={palette.secondary.contrastText}
                sx={{ pt: 1.5 }}
              >
                {t("to")}
              </Typography>
              <DatePicker
                label={""}
                disableFuture
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    color: "primary",
                    placeholder: t("select", { name: t("date") }),
                    label: "",
                    margin: "none",
                    sx: toolbarSearchSx,
                    inputProps: {
                      style: { padding: 8.5 },
                    },
                  },
                }}
              />
            </Stack>
          )}
          {setSearchQuery && !mobile && (
            <Box sx={{ mx: 2, display: "flex", alignItems: "center" }}>
              <TextField
                placeholder={capitalize(t("search"))}
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  borderRadius: 0.5,
                  backgroundColor: "white",
                  width: "100%",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                margin="none"
                size="small"
              />
            </Box>
          )}
          {createLink && (
            <IconButton
              href={createLink}
              onClick={onCreateClick}
              sx={{
                color: palette.primary.main,
                borderColor: palette.primary.contrastText,
                borderWidth: 1,
                borderRadius: 90,
              }}
            >
              <AddCircle
                color={"primary"}
                fontSize="large"
                sx={{
                  background: palette.primary.contrastText,
                  borderRadius: "90px",
                }}
              />
            </IconButton>
          )}
          {loading ? (
            <Box
              sx={{
                ml: "auto",
                pr: "8px",
                py: "8px",
                display: "flex",
                justifyContent: "center",
                alignContent: "center",
              }}
            >
              <CircularProgress
                size={35}
                sx={{
                  color: palette.primary.contrastText,
                  backgroundColor: palette.primary.main,
                  borderRadius: 90,
                }}
              />
            </Box>
          ) : (
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandCircleDown
                color={"primary"}
                fontSize="large"
                sx={{
                  background: palette.primary.contrastText,
                  borderRadius: "90px",
                }}
              />
            </ExpandMore>
          )}
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {mobile && setSearchQuery && (
          <Box sx={{ flex: 1, display: "flex", py: 1, px: 4, ...cardHeaderSx }}>
            <TextField
              placeholder={capitalize(t("search"))}
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={toolbarSearchSx}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              margin="none"
              size="small"
            />
          </Box>
        )}
        {mobile && enableDateRange && (
          <Box sx={{ flex: 1, display: "flex", py: 1, px: 4, ...cardHeaderSx }}>
            <Stack spacing={1} direction={"row"} p={0}>
              <DatePicker
                disableFuture
                value={startDate}
                onChange={setStartDate}
                views={["year", "month", "day"]}
                slotProps={{
                  textField: {
                    color: "primary",
                    placeholder: t("select", { name: t("date") }),
                    label: "",
                    margin: "none",
                    sx: toolbarSearchSx,
                    inputProps: {
                      style: { padding: 8.5, textAlign: "center" },
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                color={palette.secondary.contrastText}
                sx={{ pt: 1.5 }}
              >
                {t("to")}
              </Typography>
              <DatePicker
                label={""}
                disableFuture
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    color: "primary",
                    placeholder: t("select", { name: t("date") }),
                    label: "",
                    margin: "none",
                    sx: toolbarSearchSx,
                    inputProps: {
                      style: { padding: 8.5, textAlign: "center" },
                    },
                  },
                }}
              />
            </Stack>
          </Box>
        )}
        <Box
          sx={{
            p: padding,
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Card>
  );
};

export default CollapsableBox;
